import { useEffect, useState } from 'react';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { GroupFilterSelect, UNGROUPED_LABEL } from '@/components/form/GroupFilterSelect';
import { SearchBox } from '@/components/form/SearchBox';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useGroupOptions } from '@/hooks/useGroupOptions';
import { useAsyncData } from '@/hooks/useAsyncData';
import { showToast } from '@/lib/toast';
import { formatBalance } from '@/lib/clientFormat';
import {
  fetchTestCatalog,
  fetchTestCatalogGroups,
  updateTestCatalogCost,
  fetchTestReports,
  updateTestReportCost,
  type TestCatalogRow,
  type TestReportRow,
} from '@/api/testCatalog';

type Scope = 'library' | 'monoscale';

const SECTIONS: { scope: Scope; title: string }[] = [
  { scope: 'library', title: 'Тесты' },
  { scope: 'monoscale', title: 'Моношкальные тесты' },
];

/**
 * Каталог — глобальный прайс библиотеки тестов, отдельно от какого-либо
 * клиента (1:1 старый Orchid-раздел "Тарифы", app/Orchid/Screens/Tarif/*.php
 * в старой админке). Тесты (с отчётами, см. CatalogTile) и моношкальные
 * (без отчётов — см. докблок Laravel TestCatalogController::reports()) —
 * две вкладки, не два блока подряд (моношкальные не связаны с обычными
 * тестами содержательно, а раскрытие отчётов у обычных тестов вытягивает
 * страницу — два независимых списка рядом только мешали бы). Права отдельные от
 * создания/редактирования клиентов (platform*.tarif) — см. докблоки
 * Laravel-эндпоинтов.
 */
export function TestsCatalogPage() {
  const [scope, setScope] = useState<Scope>('library');

  return (
    <section className="catalog">
      <header className="catalog__header">
        <h1>Каталог</h1>
        <p className="catalog__lede">
          Базовая стоимость тестов и отчётов — общая для всех клиентов, если для конкретного не задана своя.
        </p>
      </header>

      <div className="card tabs">
        <div className="tabs__list" role="tablist">
          {SECTIONS.map((s) => (
            <button
              key={s.scope}
              type="button"
              role="tab"
              aria-selected={scope === s.scope}
              className={`tabs__item${scope === s.scope ? ' tabs__item--active' : ''}`}
              onClick={() => setScope(s.scope)}
            >
              {s.title}
            </button>
          ))}
        </div>

        <div className="tabs__panel" role="tabpanel">
          <CatalogSection key={scope} scope={scope} />
        </div>
      </div>
    </section>
  );
}

function CatalogSection({ scope }: { scope: Scope }) {
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [group, setGroup] = useState('');
  const [page, setPage] = useState(1);
  const { groups, ungroupedValue } = useGroupOptions(() => fetchTestCatalogGroups(scope), [scope]);
  const [pendingId, setPendingId] = useState<number | null>(null);

  useEffect(() => {
    setPage(1);
  }, [search, group]);

  const {
    data: list,
    error,
    setData,
  } = useAsyncData(
    () => fetchTestCatalog(scope, { search: search || undefined, group: group || undefined, page }),
    [scope, search, group, page],
    'Не удалось загрузить каталог.',
  );
  const rows = list?.items ?? null;
  const meta = list?.meta ?? null;

  const saveCost = (row: TestCatalogRow, cost: number) => {
    setPendingId(row.id);
    updateTestCatalogCost(row.id, cost)
      .then((updated) => {
        setData((prev) =>
          prev ? { ...prev, items: prev.items.map((r) => (r.id === row.id ? updated : r)) } : prev,
        );
        showToast(`Стоимость «${row.name}» обновлена`);
      })
      .catch(() => showToast(`Не удалось изменить стоимость «${row.name}».`))
      .finally(() => setPendingId(null));
  };

  return (
    <div className="catalog-section">
      <div className="filter-row">
        <SearchBox value={searchInput} onChange={setSearchInput} />
        <GroupFilterSelect groups={groups} ungroupedValue={ungroupedValue} value={group} onChange={setGroup} />
      </div>

      {error && <p className="field-error">{error}</p>}

      {rows === null ? (
        <div className="panel-loading">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <p className="cell-muted">Тесты не найдены.</p>
      ) : (
        <>
          <div className="catalog-tiles">
            {rows.map((row) => (
              <CatalogTile
                key={row.id}
                row={row}
                scope={scope}
                pending={pendingId === row.id}
                onSaveCost={(cost) => saveCost(row, cost)}
              />
            ))}
          </div>

          {meta && meta.lastPage > 1 && (
            <div className="table-footer">
              <span className="table-footer__status">
                Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
              </span>
              <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Плитка теста — название/версия/группа + инлайн-редактируемая базовая
 * стоимость (тот же приём, что PriceCell в PricesPanel.tsx). У библиотечных
 * тестов есть раскрытие — список отчётов (MainTestVariants), тоже со своей
 * стоимостью каждый; у моношкальных отчётов не бывает вовсе (см. докблок
 * TestCatalogController::reports() в Laravel), раскрытия нет.
 */
function CatalogTile({
  row,
  scope,
  pending,
  onSaveCost,
}: {
  row: TestCatalogRow;
  scope: Scope;
  pending: boolean;
  onSaveCost: (cost: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [reports, setReports] = useState<TestReportRow[] | null>(null);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [pendingReportId, setPendingReportId] = useState<number | null>(null);

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && reports === null) {
      fetchTestReports(row.id)
        .then(setReports)
        .catch(() => setReportsError('Не удалось загрузить отчёты.'));
    }
  };

  const saveReportCost = (report: TestReportRow, cost: number) => {
    setPendingReportId(report.id);
    updateTestReportCost(row.id, report.id, cost)
      .then((updated) => {
        setReports((prev) => prev?.map((r) => (r.id === report.id ? updated : r)) ?? prev);
        showToast(`Стоимость отчёта «${report.title}» обновлена`);
      })
      .catch(() => showToast(`Не удалось изменить стоимость отчёта «${report.title}».`))
      .finally(() => setPendingReportId(null));
  };

  return (
    <div className={`catalog-tile${expanded ? ' catalog-tile--expanded' : ''}`}>
      <div className="catalog-tile__main">
        <div className="catalog-tile__icon">
          <Icon name={scope === 'monoscale' ? 'gearSpark' : 'testChecklist'} size={18} />
        </div>
        <div className="catalog-tile__body">
          <div className="catalog-tile__name">
            {row.name}
            {row.version && <span className="catalog-tile__version"> · v{row.version}</span>}
          </div>
          <div className="catalog-tile__category">{row.category ?? UNGROUPED_LABEL}</div>
        </div>
        <CostCell label={row.name} cost={row.basePrice} disabled={pending} onSave={onSaveCost} />
        {scope === 'library' && (
          <button
            type="button"
            className="catalog-tile__expand"
            onClick={toggleExpand}
            aria-expanded={expanded}
            aria-label={expanded ? `Скрыть отчёты «${row.name}»` : `Показать отчёты «${row.name}»`}
          >
            <Icon name="chevronDown" size={16} />
          </button>
        )}
      </div>

      {scope === 'library' && expanded && (
        <div className="catalog-tile__reports">
          {reportsError && <p className="field-error">{reportsError}</p>}
          {reports === null ? (
            <div className="panel-loading">
              <Spinner />
            </div>
          ) : reports.length === 0 ? (
            <p className="cell-muted">У теста нет отчётов.</p>
          ) : (
            <ul className="catalog-report-list">
              {reports.map((report) => (
                <li key={report.id} className="catalog-report-list__item">
                  <Icon name="fileText" size={15} />
                  <span className="catalog-report-list__title">{report.title}</span>
                  <CostCell
                    label={report.title}
                    cost={report.cost}
                    disabled={pendingReportId === report.id}
                    onSave={(cost) => saveReportCost(report, cost)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** Статичный текст + карандаш, по клику превращается в поле ввода — тот же приём, что PriceCell в PricesPanel.tsx. */
function CostCell({
  label,
  cost,
  disabled,
  onSave,
}: {
  label: string;
  cost: number;
  disabled: boolean;
  onSave: (cost: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(String(cost));

  if (editing) {
    return (
      <input
        type="number"
        min={0}
        autoFocus
        className="test-list__cost-input"
        value={local}
        disabled={disabled}
        onChange={(e) => setLocal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setLocal(String(cost));
            setEditing(false);
          }
        }}
        onBlur={() => {
          setEditing(false);
          const n = Number(local);
          if (Number.isFinite(n) && n >= 0 && n !== cost) onSave(n);
          else setLocal(String(cost));
        }}
        aria-label={`Стоимость: «${label}»`}
      />
    );
  }

  return (
    <span className="price-cell">
      <b>{formatBalance(cost)}</b>
      <button
        type="button"
        className="price-cell__edit"
        disabled={disabled}
        onClick={() => {
          setLocal(String(cost));
          setEditing(true);
        }}
        aria-label={`Изменить стоимость «${label}»`}
      >
        <Icon name="pencil" size={13} />
      </button>
    </span>
  );
}
